import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Users, Plus, Loader2, Trash2, Radio, Clock, DollarSign, Camera, X,
  Coffee, Power, KeyRound, ChevronRight, ChevronDown, Download, Pencil, Play,
} from 'lucide-react';
import { AdminTasksView, AdminContactsView } from '../../../components/VAAdminTasksContacts';
import { authedUrl } from '../../../lib/authSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const pwd = () => sessionStorage.getItem('utmost_admin_token') || '';

const call = async (path, { method = 'GET', body } = {}) => {
  const headers = { 'x-admin-password': pwd() };
  if (body) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API_URL}/api/timetrack${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Failed (${res.status})`); }
  return res.status === 204 ? null : res.json();
};

const money = (v, cur = 'USD') => `${cur === 'USD' ? '$' : cur + ' '}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dur = (secs) => { const s = Math.floor(secs || 0); const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); return `${h}h ${m}m`; };

// All timesheet times/dates are shown in Central Time (CST/CDT) regardless of
// the viewer's browser timezone, per company standard.
const CST_TZ = 'America/Winnipeg';
const cstTime = (v) => v ? new Date(v).toLocaleTimeString('en-US', { timeZone: CST_TZ, hour: '2-digit', minute: '2-digit' }) : '';
const cstDate = (v) => v ? new Date(v).toLocaleDateString('en-US', { timeZone: CST_TZ }) : '';
const cstDateTime = (v) => v ? new Date(v).toLocaleString('en-US', { timeZone: CST_TZ, dateStyle: 'medium', timeStyle: 'short' }) : '';
const cstToday = () => new Date().toLocaleDateString('en-CA', { timeZone: CST_TZ }); // YYYY-MM-DD

const TeamTimeTrackingTab = () => {
  const [view, setView] = useState('live'); // live | staff | timesheets
  const [staff, setStaff] = useState([]);
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', hourly_rate: '', currency: 'USD' });
  const [shots, setShots] = useState(null); // {sessionId, list}
  const [viewShot, setViewShot] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [tsStaff, setTsStaff] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const loadAll = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [s, l] = await Promise.all([call('/admin/staff'), call('/admin/live')]);
      setStaff(s); setLive(l);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (view !== 'live') return undefined;
    const id = setInterval(async () => { try { setLive(await call('/admin/live')); } catch { /* noop */ } }, 20000);
    return () => clearInterval(id);
  }, [view]);

  const addStaff = async () => {
    try {
      await call('/admin/staff', { method: 'POST', body: { ...form, hourly_rate: Number(form.hourly_rate) || 0 } });
      setShowAdd(false); setForm({ email: '', name: '', password: '', hourly_rate: '', currency: 'USD' });
      loadAll();
    } catch (e) { alert(e.message); }
  };
  const delStaff = async (id) => { if (!window.confirm('Delete this staff member? Their timesheets remain.')) return; try { await call(`/admin/staff/${id}`, { method: 'DELETE' }); loadAll(); } catch (e) { alert(e.message); } };
  const toggleActive = async (s) => { try { await call(`/admin/staff/${s.id}`, { method: 'PATCH', body: { is_active: !s.is_active } }); loadAll(); } catch (e) { alert(e.message); } };
  const resetPw = async (s) => { const np = window.prompt(`New password for ${s.name} (min 6 chars):`); if (!np) return; try { await call(`/admin/staff/${s.id}`, { method: 'PATCH', body: { password: np } }); alert('Password updated.'); } catch (e) { alert(e.message); } };

  const openShots = async (sessionId) => { try { const list = await call(`/admin/sessions/${sessionId}/screenshots`); setShots({ sessionId, list }); } catch (e) { alert(e.message); } };
  const loadTimesheets = async (staffId) => { try { const d = await call(`/admin/sessions${staffId ? `?staff_id=${staffId}` : ''}`); setSessions(d); } catch (e) { alert(e.message); } };
  const delSession = async (id) => { if (!window.confirm('Delete this timesheet entry? This also removes its screenshots. This cannot be undone.')) return; try { await call(`/admin/sessions/${id}`, { method: 'DELETE' }); loadTimesheets(tsStaff); } catch (e) { alert(e.message); } };

  useEffect(() => { if (view === 'timesheets' && sessions === null) loadTimesheets(''); }, [view]);

  // Group sessions by staff + day for the collapsible timesheet view.
  const groups = useMemo(() => {
    const list = sessions?.sessions || [];
    const map = new Map();
    for (const s of list) {
      const dateKey = cstDate(s.clock_in);
      const key = `${s.staff_id}__${dateKey}`;
      if (!map.has(key)) {
        map.set(key, {
          key, staff_id: s.staff_id, staff_name: s.staff_name, date: dateKey,
          currency: s.currency || 'USD', mixed: false, sessions: [], worked: 0, pay: 0,
        });
      }
      const g = map.get(key);
      g.sessions.push(s);
      g.worked += s.worked_seconds || 0;
      g.pay += s.pay || 0;
      if ((s.currency || 'USD') !== g.currency) g.mixed = true;
    }
    return Array.from(map.values());
  }, [sessions]);

  const toggleGroup = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const downloadReport = () => {
    const rows = [['Staff', 'Date', 'Sessions', 'Total Worked', 'Total Hours', 'Pay', 'Currency', 'Notes (all times CST)']];
    for (const g of groups) {
      const notes = g.sessions.map((s) => s.current_task).filter(Boolean).join(' | ');
      rows.push([g.staff_name, g.date, g.sessions.length, dur(g.worked), (g.worked / 3600).toFixed(2), g.pay.toFixed(2), g.mixed ? 'mixed' : g.currency, notes]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheet-daily-report-${cstToday()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0A2A43]" /></div>;

  // Currency for the Timesheets totals: use the filtered staff's currency, else
  // the shared currency of the listed sessions (null = mixed currencies).
  const totalsCurrency = (() => {
    const list = sessions?.sessions || [];
    if (tsStaff) { const s = staff.find((x) => x.id === tsStaff); if (s) return s.currency || 'USD'; }
    if (!list.length) return 'USD';
    const first = list[0].currency || 'USD';
    return list.every((x) => (x.currency || 'USD') === first) ? first : null;
  })();

  return (
    <div className="space-y-4" data-testid="team-timetracking-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Users className="h-6 w-6 text-[#0A2A43]" />
          <h2 className="text-xl font-bold text-[#0A2A43]">Team Time Tracking</h2>
        </div>
        <a href="/time-tracker" target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline">Open staff tracker ↗</a>
      </div>

      <div className="flex gap-1 border-b flex-wrap">
        {[['live', 'Live Now'], ['tasks', 'Tasks'], ['contacts', 'Call List'], ['timesheets', 'Timesheets'], ['staff', 'Staff']].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)} data-testid={`tt-tab-${id}`}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${view === id ? 'border-[#0A2A43] text-[#0A2A43]' : 'border-transparent text-gray-500'}`}>{label}</button>
        ))}
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}

      {/* TASKS */}
      {view === 'tasks' && <AdminTasksView staff={staff} />}

      {/* CALL LIST */}
      {view === 'contacts' && <AdminContactsView staff={staff} />}

      {/* LIVE */}
      {view === 'live' && (
        <div className="space-y-3" data-testid="tt-live">
          <p className="text-xs text-gray-500 flex items-center gap-1"><Radio className="h-3.5 w-3.5 text-emerald-500" /> Auto-refreshes every 20s · {live.length} clocked in</p>
          {live.length === 0 && <Card><CardContent className="p-6 text-center text-gray-500 text-sm">No one is clocked in right now.</CardContent></Card>}
          {live.map((s) => (
            <Card key={s.id}><CardContent className="p-4 flex items-center gap-4 flex-wrap">
              <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" /></span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#0A2A43]">{s.staff_name} {s.on_break && <span className="text-xs text-amber-600 font-normal">· on break</span>}</p>
                {s.current_task
                  ? <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap" data-testid={`live-notes-${s.id}`}>{s.current_task}</p>
                  : <p className="text-xs text-gray-400">No notes yet</p>}
              </div>
              <div className="text-right">
                <p className="font-bold tabular-nums text-[#0A2A43]">{dur(s.worked_seconds)}</p>
                <p className="text-[11px] text-gray-400">since {cstTime(s.clock_in)}</p>
              </div>
              {s.last_screenshot ? (
                <button onClick={() => setViewShot(s.last_screenshot.id)} className="w-24 h-16 rounded border overflow-hidden bg-gray-100 hover:ring-2 ring-[#0A2A43]" data-testid={`tt-live-shot-${s.id}`}>
                  <img alt="latest" src={authedUrl(`${API_URL}/api/timetrack/admin/screenshots/${s.last_screenshot.id}/view`)} className="w-full h-full object-cover" />
                </button>
              ) : <span className="text-[11px] text-gray-400 w-24 text-center">No screenshot yet</span>}
              <Button size="sm" variant="outline" onClick={() => openShots(s.id)}><Camera className="h-4 w-4 mr-1" /> Shots</Button>
            </CardContent></Card>
          ))}
        </div>
      )}

      {/* TIMESHEETS */}
      {view === 'timesheets' && (
        <div className="space-y-3" data-testid="tt-timesheets">
          <div className="flex items-center gap-2 flex-wrap">
            <select value={tsStaff} onChange={(e) => { setTsStaff(e.target.value); loadTimesheets(e.target.value); }} className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="tt-staff-filter">
              <option value="">All staff</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <Button size="sm" variant="outline" onClick={downloadReport} disabled={!groups.length} data-testid="tt-download-report">
              <Download className="h-4 w-4 mr-1" /> Daily report (CSV)
            </Button>
            <span className="text-[11px] text-gray-400 ml-auto" data-testid="tt-cst-note">All times shown in CST (Central Time)</span>
          </div>
          {sessions && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="p-4 text-center"><Clock className="h-5 w-5 mx-auto text-[#0A2A43] mb-1" /><p className="text-2xl font-bold text-[#0A2A43]">{sessions.totals.worked_hours}h</p><p className="text-xs text-gray-500">Total hours</p></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><DollarSign className="h-5 w-5 mx-auto text-emerald-600 mb-1" /><p className="text-2xl font-bold text-emerald-600" data-testid="tt-total-pay">{totalsCurrency ? money(sessions.totals.pay, totalsCurrency) : `${Number(sessions.totals.pay).toFixed(2)} (mixed)`}</p><p className="text-xs text-gray-500">Total pay</p></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><Users className="h-5 w-5 mx-auto text-[#0A2A43] mb-1" /><p className="text-2xl font-bold text-[#0A2A43]">{sessions.totals.sessions}</p><p className="text-xs text-gray-500">Sessions</p></CardContent></Card>
              </div>

              {groups.length === 0 && <Card><CardContent className="p-6 text-center text-gray-400 text-sm">No sessions yet.</CardContent></Card>}

              <div className="space-y-2" data-testid="tt-daily-groups">
                {groups.map((g) => {
                  const open = expanded.has(g.key);
                  return (
                    <Card key={g.key} data-testid={`tt-day-group-${g.key}`}>
                      <button
                        onClick={() => toggleGroup(g.key)}
                        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
                        data-testid={`tt-day-toggle-${g.key}`}
                      >
                        {open ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#0A2A43] truncate">{g.staff_name}</p>
                          <p className="text-xs text-gray-500">{g.date} · {g.sessions.length} session{g.sessions.length !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold tabular-nums text-[#0A2A43]" data-testid={`tt-day-worked-${g.key}`}>{dur(g.worked)}</p>
                          <p className="text-xs font-semibold text-emerald-600">{g.mixed ? `${g.pay.toFixed(2)} (mixed)` : money(g.pay, g.currency)}</p>
                        </div>
                      </button>
                      {open && (
                        <div className="border-t overflow-x-auto">
                          <table className="w-full text-sm" data-testid={`tt-day-sessions-${g.key}`}>
                            <thead><tr className="text-left text-xs text-gray-500 border-b bg-gray-50">
                              <th className="p-3">In / Out</th><th className="p-3">Worked</th><th className="p-3">Pay</th><th className="p-3">Notes</th><th className="p-3 text-right">Actions</th>
                            </tr></thead>
                            <tbody>
                              {g.sessions.map((s) => (
                                <tr key={s.id} className="border-b last:border-0">
                                  <td className="p-3 text-gray-600">{cstTime(s.clock_in)} – {s.clock_out ? cstTime(s.clock_out) : '…'}</td>
                                  <td className="p-3 tabular-nums">{dur(s.worked_seconds)}{s.break_seconds > 0 && <span className="text-[11px] text-gray-400 flex items-center gap-0.5"><Coffee className="h-3 w-3" />{dur(s.break_seconds)}</span>}</td>
                                  <td className="p-3 font-semibold text-emerald-600">{money(s.pay, s.currency)}</td>
                                  <td className="p-3 text-gray-600 max-w-[280px] whitespace-pre-wrap align-top" data-testid={`ts-notes-${s.id}`}>{s.current_task || '—'}</td>
                                  <td className="p-3">
                                    <div className="flex items-center justify-end gap-1">
                                      <Button size="sm" variant="ghost" onClick={() => openShots(s.id)} title="Screenshots"><Camera className="h-4 w-4" /></Button>
                                      <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => delSession(s.id)} title="Delete entry" data-testid={`tt-del-session-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* STAFF */}
      {view === 'staff' && (
        <div className="space-y-3" data-testid="tt-staff">
          <Button onClick={() => setShowAdd(true)} className="bg-[#0A2A43] text-white" data-testid="tt-add-staff-btn"><Plus className="h-4 w-4 mr-1" /> Add Staff / VA</Button>
          {staff.map((s) => (
            <Card key={s.id}><CardContent className="p-4 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#0A2A43]">{s.name} {s.is_clocked_in && <span className="text-[11px] text-emerald-600">● online</span>} {!s.is_active && <span className="text-[11px] text-rose-500">disabled</span>}</p>
                <p className="text-xs text-gray-500">{s.email} · {money(s.hourly_rate, s.currency)}/hr</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => resetPw(s)}><KeyRound className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => toggleActive(s)}><Power className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => delStaff(s.id)}><Trash2 className="h-4 w-4" /></Button>
            </CardContent></Card>
          ))}
          {staff.length === 0 && <Card><CardContent className="p-6 text-center text-gray-500 text-sm">No staff yet. Add your virtual assistant to get started.</CardContent></Card>}
        </div>
      )}

      {/* Add staff modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="tt-add-modal">
            <h3 className="font-bold text-[#0A2A43] mb-4">Add Staff / Virtual Assistant</h3>
            <div className="space-y-3">
              <input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="tt-new-name" />
              <input placeholder="Email (login)" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="tt-new-email" />
              <input placeholder="Temporary password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="tt-new-password" />
              <div className="flex gap-2">
                <input placeholder="Hourly rate" type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="tt-new-rate" />
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white"><option>USD</option><option>CAD</option><option>PHP</option></select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={addStaff} className="bg-[#0A2A43] text-white" data-testid="tt-save-staff">Create</Button>
            </div>
          </div>
        </div>
      )}

      {/* Screenshots gallery modal */}
      {shots && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShots(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-3xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tt-shots-modal">
            <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-[#0A2A43]">Screenshots ({shots.list.length})</h3><button onClick={() => setShots(null)}><X className="h-5 w-5" /></button></div>
            {shots.list.length === 0 ? <p className="text-sm text-gray-500 text-center py-8">No screenshots captured for this session.</p> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {shots.list.map((sh) => (
                  <button key={sh.id} onClick={() => setViewShot(sh.id)} className="border rounded-lg overflow-hidden hover:ring-2 ring-[#0A2A43]">
                    <img alt="shot" src={authedUrl(`${API_URL}/api/timetrack/admin/screenshots/${sh.id}/view`)} className="w-full h-28 object-cover bg-gray-100" />
                    <p className="text-[10px] text-gray-500 p-1">{cstDateTime(sh.created_at)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full screenshot viewer */}
      {viewShot && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4" onClick={() => setViewShot(null)} data-testid="tt-shot-viewer">
          <img alt="full" src={authedUrl(`${API_URL}/api/timetrack/admin/screenshots/${viewShot}/view`)} className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
};

export default TeamTimeTrackingTab;
